/**
 * @name produtoImagemUtils
 * @description Utilitários para gerenciamento de imagens de produtos
 * @package src/utils/produtoImagemUtils.ts
 */

/**
 * Mapeamento de palavras-chave para emojis de produtos
 */
const EMOJI_MAP: Record<string, string> = {
  // Lanches e principais
  lanche: "🍔",
  hamburger: "🍔",
  sanduiche: "🥪",
  sandwich: "🥪",
  hotdog: "🌭",
  cachorro: "🌭",
  pizza: "🍕",

  // Bebidas
  bebida: "🥤",
  refrigerante: "🥤",
  refri: "🥤",
  suco: "🧃",
  agua: "💧",
  cafe: "☕",
  cha: "🍵",
  mate: "🧉",

  // Doces e sobremesas
  doce: "🍭",
  chocolate: "🍫",
  bala: "🍬",
  chiclete: "🫧",
  pirulito: "🍭",
  sorvete: "🍦",
  sorvetes: "🍦",
  gelato: "🍦",
  picole: "🍦",
  açai: "🍦",
  acai: "🍦",
  torta: "🍰",
  bolo: "🎂",
  pudim: "🍮",
  brigadeiro: "🍫",

  // Salgados
  salgado: "🥙",
  coxinha: "🥟",
  pastel: "🥟",
  empada: "🥧",
  esfirra: "🥙",
  quibe: "🥙",

  // Frutas
  fruta: "🍎",
  maca: "🍎",
  banana: "🍌",
  laranja: "🍊",
  uva: "🍇",

  // Pães e padaria
  pao: "🍞",
  padaria: "🥖",
  biscoito: "🍪",
  bolacha: "🍪",

  // Outros
  combo: "🍽️",
  pacote: "📦",
  kit: "🎁",
  natural: "🥗",
  salada: "🥗",
};

/**
 * Determina o emoji padrão baseado na categoria do produto
 *
 * @param categoria - Nome da categoria do produto
 * @param isCombo - Se é um combo/pacote
 * @returns Emoji representativo da categoria
 */
export const getEmojiPorCategoria = (
  categoria?: string,
  isCombo?: boolean,
): string => {
  if (isCombo) return "🍽️";

  if (!categoria) return "🍔";

  const categoriaLower = categoria.toLowerCase();

  // Buscar emoji exato primeiro
  if (EMOJI_MAP[categoriaLower]) {
    return EMOJI_MAP[categoriaLower];
  }

  // Buscar por palavra-chave na categoria
  for (const [palavra, emoji] of Object.entries(EMOJI_MAP)) {
    if (categoriaLower.includes(palavra)) {
      return emoji;
    }
  }

  // Emoji padrão
  return "🍔";
};

/**
 * Valida se uma URL de imagem é válida
 *
 * @param url - URL da imagem para validar
 * @returns Promise<boolean> indicando se a imagem é válida
 */
export const validarImagemUrl = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

/**
 * Gera uma descrição para alt text da imagem baseada no produto
 *
 * @param nome - Nome do produto
 * @param categoria - Categoria do produto
 * @returns String de alt text descritiva
 */
export const getAltTextProduto = (nome: string, categoria?: string): string => {
  if (categoria) {
    return `${nome} - ${categoria}`;
  }
  return `Imagem do produto ${nome}`;
};

/**
 * Lista de extensões de imagem suportadas
 */
export const EXTENSOES_IMAGEM_SUPORTADAS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".svg",
];

/**
 * Verifica se a URL tem uma extensão de imagem válida
 *
 * @param url - URL para verificar
 * @returns boolean indicando se a extensão é válida
 */
export const temExtensaoImagemValida = (url: string): boolean => {
  if (!url) return false;

  const urlLower = url.toLowerCase();
  return EXTENSOES_IMAGEM_SUPORTADAS.some((ext) => urlLower.includes(ext));
};
